import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const MissionValuesSection = () => {
  const coreValues = [
    {
      icon: "DollarSign",
      title: "Cost-Effective Solutions",
      description: "Providing high-quality medical equipment at competitive prices, ensuring healthcare facilities can access the best technology without compromising their budgets.",
      color: "success"
    },
    {
      icon: "Shield",
      title: "GDPMD Compliance",
      description: "Maintaining strict adherence to Good Distribution Practice for Medical Devices, ensuring all products meet the highest safety and quality standards.",
      color: "primary"
    },
    {
      icon: "Heart",
      title: "Ethics & Trust",
      description: "Building lasting relationships through transparency, respect, and unwavering commitment to ethical business practices in the medical industry.",
      color: "accent"
    }
  ];

  const missionStatement = `To be Malaysia's premier distributor of innovative medical technology, 
  empowering healthcare professionals with cutting-edge endoscopic and surgical solutions that 
  improve patient outcomes and advance medical care across the nation.`;

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-cyan-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="max-w-7xl mx-auto px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Our Mission & Values
          </h2>
          <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Guided by our commitment to excellence, innovation, and ethical practices in medical technology distribution.
          </p>
        </motion.div>

        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-8 md:p-12 shadow-medical mb-16 border border-gray-200 dark:border-gray-700"
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Target" size={32} color="white" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">Our Mission</h3>
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed max-w-4xl mx-auto">
              {missionStatement}
            </p>
          </div>
        </motion.div>

        {/* Core Values */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="grid lg:grid-cols-3 gap-8"
        >
          {coreValues?.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group bg-white dark:bg-gray-800 rounded-xl p-8 shadow-medical hover:shadow-medical-hover transition-all duration-300 border border-gray-200 dark:border-gray-700"
            >
              <div className="text-center">
                <div className={`w-16 h-16 bg-${value?.color}/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon 
                    name={value?.icon} 
                    size={28} 
                    color={`var(--color-${value?.color})`} 
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{value?.title}</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{value?.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Vision Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-primary/5 via-accent/5 to-success/5 dark:from-primary/10 dark:via-accent/10 dark:to-success/10 rounded-2xl p-8 md:p-12 border border-gray-200 dark:border-gray-700">
            <div className="w-16 h-16 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Eye" size={32} color="white" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">Our Vision</h3>
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed max-w-4xl mx-auto">
              To transform healthcare delivery in Malaysia by making advanced medical technology 
              accessible to every healthcare facility, fostering innovation, and setting new standards 
              for excellence in medical device distribution.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MissionValuesSection;